import { useState } from "react";

export const usePanelDashboardModalState = ({
  canAddDataForChild,
  checkForPatterns,
  setCurrentChildId,
}) => {
  const [showQuickEntry, setShowQuickEntry] = useState(false);
  const [selectedChild, setSelectedChild] = useState(null);
  const [entryType, setEntryType] = useState("micro");
  const [quickEntryStep, setQuickEntryStep] = useState(0);
  const [quickEntryDate, setQuickEntryDate] = useState(null);
  const [quickEntryInitialText, setQuickEntryInitialText] = useState('');
  const [quickEntryInitialAction, setQuickEntryInitialAction] = useState(null);
  const [quickEntryInitialPhotoFile, setQuickEntryInitialPhotoFile] = useState(null);
  const [quickEntryInitialPhotoIntent, setQuickEntryInitialPhotoIntent] = useState('');
  const [quickEntryAutoSubmit, setQuickEntryAutoSubmit] = useState(false);
  const [showDailyCareModal, setShowDailyCareModal] = useState(false);
  const [dailyCareAction, setDailyCareAction] = useState(null);
  const [dailyCareChild, setDailyCareChild] = useState(null);
  const [dailyCareEditEntry, setDailyCareEditEntry] = useState(null);
  const [showSleepLogSheet, setShowSleepLogSheet] = useState(false);
  const [sleepLogChild, setSleepLogChild] = useState(null);
  const [sleepLogEditEntry, setSleepLogEditEntry] = useState(null);
  const [sleepLogType, setSleepLogType] = useState('night');
  const [showFoodLogSheet, setShowFoodLogSheet] = useState(false);
  const [foodLogChild, setFoodLogChild] = useState(null);
  const [foodLogEditEntry, setFoodLogEditEntry] = useState(null);
  const [showBathroomLogSheet, setShowBathroomLogSheet] = useState(false);
  const [bathroomLogChild, setBathroomLogChild] = useState(null);
  const [bathroomLogEditEntry, setBathroomLogEditEntry] = useState(null);
  const [showIncidentModal, setShowIncidentModal] = useState(false);
  const [incidentChild, setIncidentChild] = useState(null);
  const [incidentEditEntry, setIncidentEditEntry] = useState(null);
  const [showFollowUpModal, setShowFollowUpModal] = useState(false);
  const [followUpIncident, setFollowUpIncident] = useState(null);
  const [showPatternSuggestionModal, setShowPatternSuggestionModal] = useState(false);
  const [patternSuggestions, setPatternSuggestions] = useState([]);
  const [suggestionsChildId, setSuggestionsChildId] = useState(null);
  const [showDailyHabitsModal, setShowDailyHabitsModal] = useState(false);
  const [dailyHabitsChild, setDailyHabitsChild] = useState(null);
  const [dailyHabitsInitialCategoryId, setDailyHabitsInitialCategoryId] = useState(null);
  const [dailyHabitsEditEntry, setDailyHabitsEditEntry] = useState(null);
  const [showMedicalLogPanel, setShowMedicalLogPanel] = useState(false);
  const [medicalLogChild, setMedicalLogChild] = useState(null);
  const [medicalLogEditEntry, setMedicalLogEditEntry] = useState(null);
  const [quickEntryEditEntry, setQuickEntryEditEntry] = useState(null);
  const [quickEntryMode, setQuickEntryMode] = useState('create');
  const [showSmartCaptureNoteSheet, setShowSmartCaptureNoteSheet] = useState(false);
  const [smartCaptureNoteChild, setSmartCaptureNoteChild] = useState(null);
  const [smartCaptureNoteEditEntry, setSmartCaptureNoteEditEntry] = useState(null);
  const [showTimelineNoteEditSheet, setShowTimelineNoteEditSheet] = useState(false);
  const [timelineNoteEditChild, setTimelineNoteEditChild] = useState(null);
  const [timelineNoteEditEntry, setTimelineNoteEditEntry] = useState(null);
  const [showMoodSheet, setShowMoodSheet] = useState(false);
  const [moodSheetChild, setMoodSheetChild] = useState(null);
  const [moodSheetEntry, setMoodSheetEntry] = useState(null);

  const handleQuickDataEntry = (
    child,
    type,
    event,
    dateValue = null,
    initialText = '',
    initialAction = null,
    initialPhotoFile = null,
    options = {}
  ) => {
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }

    if (!canAddDataForChild(child.id)) {
      console.log("User does not have permission to add data for this child");
      return;
    }

    setCurrentChildId(child.id);

    if (type === "mood") {
      setDailyCareAction(type);
      setDailyCareChild(child);
      setShowDailyCareModal(true);
      return;
    }

    if (type === "sleep") {
      setSleepLogChild(child);
      setSleepLogEditEntry(null);
      setSleepLogType('night');
      setShowSleepLogSheet(true);
      return;
    }

    if (type === "medication" || type === "diaper") {
      setDailyHabitsChild(child);
      setDailyHabitsInitialCategoryId(null);
      setShowDailyHabitsModal(true);
      return;
    }

    if (type === "activity") {
      setDailyCareAction(type);
      setDailyCareChild(child);
      setDailyCareEditEntry(null);
      setShowDailyCareModal(true);
      return;
    }

    if (type === "incident") {
      setIncidentChild(child);
      setIncidentEditEntry(null);
      setShowIncidentModal(true);
      return;
    }

    if (type === "dailyLog") {
      setSelectedChild(child);
      setEntryType("full");
      setQuickEntryStep(0);
      setQuickEntryDate(dateValue || null);
      setQuickEntryInitialText(String(initialText || '').trim());
      setQuickEntryInitialAction(initialAction || null);
      setQuickEntryInitialPhotoFile(initialPhotoFile || null);
      setQuickEntryInitialPhotoIntent(options?.photoIntent || '');
      setQuickEntryAutoSubmit(Boolean(options?.autoSubmit));
      setQuickEntryMode('create');
      setQuickEntryEditEntry(null);
      setShowQuickEntry(true);
      return;
    }

    setSelectedChild(child);
    setEntryType("full");
    setQuickEntryDate(dateValue || null);
    setQuickEntryInitialText(String(initialText || '').trim());
    setQuickEntryInitialAction(initialAction || null);
    setQuickEntryInitialPhotoFile(initialPhotoFile || null);
    setQuickEntryInitialPhotoIntent(options?.photoIntent || '');
    setQuickEntryAutoSubmit(Boolean(options?.autoSubmit));
    setQuickEntryMode('create');
    setQuickEntryEditEntry(null);
    setQuickEntryStep(type === "quick_note" ? 2 : 0);
    setShowQuickEntry(true);
  };

  const handleCloseDailyCareModal = () => {
    setShowDailyCareModal(false);
    setDailyCareAction(null);
    setDailyCareChild(null);
    setDailyCareEditEntry(null);
  };

  const handleCloseMoodSheet = () => {
    setShowMoodSheet(false);
    setMoodSheetChild(null);
    setMoodSheetEntry(null);
  };

  const handleCloseSleepLogSheet = () => {
    setShowSleepLogSheet(false);
    setSleepLogChild(null);
    setSleepLogEditEntry(null);
    setSleepLogType('night');
  };

  const handleCloseFoodLogSheet = () => {
    setShowFoodLogSheet(false);
    setFoodLogChild(null);
    setFoodLogEditEntry(null);
  };

  const handleCloseBathroomLogSheet = () => {
    setShowBathroomLogSheet(false);
    setBathroomLogChild(null);
    setBathroomLogEditEntry(null);
  };

  const handleCloseMedicalLog = () => {
    setShowMedicalLogPanel(false);
    setMedicalLogChild(null);
    setMedicalLogEditEntry(null);
  };

  const handleCloseSmartCaptureNoteSheet = () => {
    setShowSmartCaptureNoteSheet(false);
    setSmartCaptureNoteChild(null);
    setSmartCaptureNoteEditEntry(null);
  };

  const handleCloseTimelineNoteEditSheet = () => {
    setShowTimelineNoteEditSheet(false);
    setTimelineNoteEditChild(null);
    setTimelineNoteEditEntry(null);
  };

  const handleCloseIncidentModal = () => {
    setShowIncidentModal(false);
    const childId = incidentChild?.id;
    setIncidentChild(null);
    setIncidentEditEntry(null);

    if (childId) {
      setTimeout(() => {
        checkForPatterns(childId);
      }, 1000);
    }
  };

  const handleCloseFollowUpModal = () => {
    setShowFollowUpModal(false);
    setFollowUpIncident(null);
  };

  const handleClosePatternSuggestionModal = () => {
    setShowPatternSuggestionModal(false);
    setPatternSuggestions([]);
    setSuggestionsChildId(null);
  };

  const handleCloseDailyHabitsModal = () => {
    setShowDailyHabitsModal(false);
    setDailyHabitsChild(null);
    setDailyHabitsInitialCategoryId(null);
    setDailyHabitsEditEntry(null);
  };

  const handleOpenMedicalLog = (child) => {
    if (!child) {
      return;
    }

    setCurrentChildId(child.id);
    setMedicalLogChild(child);
    setMedicalLogEditEntry(null);
    setShowMedicalLogPanel(true);
  };

  const handleEditMedicalLog = (child, entry = null) => {
    if (!child) {
      return;
    }

    setCurrentChildId(child.id);
    setMedicalLogChild(child);
    setMedicalLogEditEntry(entry);
    setShowMedicalLogPanel(true);
  };

  const handleOpenSleepLog = (child, entry = null, options = {}) => {
    if (!child || !canAddDataForChild?.(child.id)) {
      return;
    }

    setCurrentChildId(child.id);
    setSleepLogChild(child);
    setSleepLogEditEntry(entry);
    setSleepLogType(options?.sleepType || entry?.sleepDetails?.type || entry?.originalData?.sleepDetails?.type || 'night');
    setShowSleepLogSheet(true);
  };

  const handleTrack = (child, initialCategoryId = null) => {
    if (!child || !canAddDataForChild?.(child.id)) {
      return;
    }

    setCurrentChildId(child.id);

    if (initialCategoryId === 'medication') {
      return;
    }

    if (initialCategoryId === 'sleep') {
      setSleepLogChild(child);
      setSleepLogType('night');
      setShowSleepLogSheet(true);
      return;
    }

    if (initialCategoryId === 'food') {
      setFoodLogChild(child);
      setShowFoodLogSheet(true);
      return;
    }

    if (initialCategoryId === 'bathroom' || initialCategoryId === 'toileting' || initialCategoryId === 'diaper') {
      setBathroomLogChild(child);
      setShowBathroomLogSheet(true);
      return;
    }

    setDailyHabitsChild(child);
    setDailyHabitsInitialCategoryId(initialCategoryId || null);
    setShowDailyHabitsModal(true);
  };

  const handleOpenFoodLog = (child, entry = null) => {
    if (!child || !canAddDataForChild?.(child.id)) {
      return;
    }

    setCurrentChildId(child.id);
    setFoodLogChild(child);
    setFoodLogEditEntry(entry);
    setShowFoodLogSheet(true);
  };

  const handleOpenBathroomLog = (child, entry = null) => {
    if (!child || !canAddDataForChild?.(child.id)) {
      return;
    }

    setCurrentChildId(child.id);
    setBathroomLogChild(child);
    setBathroomLogEditEntry(entry);
    setShowBathroomLogSheet(true);
  };

  return {
    bathroomLogChild,
    bathroomLogEditEntry,
    dailyCareAction,
    dailyCareChild,
    dailyCareEditEntry,
    dailyHabitsChild,
    dailyHabitsEditEntry,
    dailyHabitsInitialCategoryId,
    entryType,
    followUpIncident,
    foodLogChild,
    foodLogEditEntry,
    handleCloseBathroomLogSheet,
    handleCloseDailyCareModal,
    handleCloseDailyHabitsModal,
    handleCloseFollowUpModal,
    handleCloseFoodLogSheet,
    handleCloseIncidentModal,
    handleCloseMedicalLog,
    handleCloseSmartCaptureNoteSheet,
    handleCloseTimelineNoteEditSheet,
    handleCloseMoodSheet,
    handleClosePatternSuggestionModal,
    handleCloseSleepLogSheet,
    handleEditMedicalLog,
    handleOpenBathroomLog,
    handleOpenFoodLog,
    handleOpenMedicalLog,
    handleOpenSleepLog,
    handleQuickDataEntry,
    handleTrack,
    incidentChild,
    incidentEditEntry,
    medicalLogChild,
    medicalLogEditEntry,
    smartCaptureNoteChild,
    smartCaptureNoteEditEntry,
    timelineNoteEditChild,
    timelineNoteEditEntry,
    moodSheetChild,
    moodSheetEntry,
    patternSuggestions,
    quickEntryDate,
    quickEntryAutoSubmit,
    quickEntryInitialAction,
    quickEntryInitialPhotoFile,
    quickEntryInitialPhotoIntent,
    quickEntryInitialText,
    quickEntryEditEntry,
    quickEntryMode,
    quickEntryStep,
    selectedChild,
    setBathroomLogChild,
    setBathroomLogEditEntry,
    setDailyCareAction,
    setDailyCareChild,
    setDailyCareEditEntry,
    setDailyHabitsChild,
    setDailyHabitsEditEntry,
    setDailyHabitsInitialCategoryId,
    setEntryType,
    setFollowUpIncident,
    setFoodLogChild,
    setFoodLogEditEntry,
    setIncidentChild,
    setIncidentEditEntry,
    setMedicalLogChild,
    setMedicalLogEditEntry,
    setShowSmartCaptureNoteSheet,
    setSmartCaptureNoteChild,
    setSmartCaptureNoteEditEntry,
    setShowTimelineNoteEditSheet,
    setTimelineNoteEditChild,
    setTimelineNoteEditEntry,
    setMoodSheetChild,
    setMoodSheetEntry,
    setPatternSuggestions,
    setQuickEntryDate,
    setQuickEntryAutoSubmit,
    setQuickEntryInitialAction,
    setQuickEntryInitialPhotoFile,
    setQuickEntryInitialText,
    setQuickEntryEditEntry,
    setQuickEntryMode,
    setQuickEntryStep,
    setSelectedChild,
    setShowBathroomLogSheet,
    setShowDailyCareModal,
    setShowDailyHabitsModal,
    setShowFollowUpModal,
    setShowFoodLogSheet,
    setShowIncidentModal,
    setShowMedicalLogPanel,
    setShowMoodSheet,
    setShowPatternSuggestionModal,
    setShowQuickEntry,
    setShowSleepLogSheet,
    setSleepLogChild,
    setSleepLogEditEntry,
    setSleepLogType,
    setSuggestionsChildId,
    showBathroomLogSheet,
    showDailyCareModal,
    showDailyHabitsModal,
    showFollowUpModal,
    showFoodLogSheet,
    showIncidentModal,
    showMedicalLogPanel,
    showSmartCaptureNoteSheet,
    showTimelineNoteEditSheet,
    showMoodSheet,
    showPatternSuggestionModal,
    showQuickEntry,
    showSleepLogSheet,
    sleepLogChild,
    sleepLogEditEntry,
    sleepLogType,
    suggestionsChildId,
  };
};
